/**
 * The world's land (D267): where the yard, each producer's plot, the festival marks, trees and the
 * stream sit. Made once from the world's seed when the world is made, and stored on the state, so a
 * world's map never moves when the content's default plan is changed. Nothing that earns reads it.
 *
 * The default plan is `content.map`. A seed may flip it east to west, and puts trees and, mostly,
 * a stream in the margin outside the world: never behind it, where the festival marks' rows grow.
 * The same seed always gives the same layout.
 *
 * D287: every world has a river. A world with a stream has the stream as its river; one without is
 * given a river from its seed, on its own seeded draw, so the rest of its layout does not move.
 * Trees never stand in the river.
 *
 * D300, D301: a layout stored from a smaller plan gains the plan's new plots on read, and every
 * layout gains the content's pieces of land beside the world, each on its own seeded draw.
 *
 * D423: a kind's world has a yard 6 by 6, room for the world's heart. A kind's layout stored with
 * the older, shallower yard is laid again from its own seed on read (`idleRelaid`).
 *
 * D440: the plan's own yard is 6 by 6 too, so the heart is one size in every world. A plan layout
 * stored with any other yard is laid again on read: seed 0 to the default layout, any other seed
 * to the classic layout for that seed.
 */
import type { IdleContent } from '@hedgerow/data';
import type {
  IdleWorldState,
  IdleLayout,
  IdleLayoutLand,
  IdleLayoutRect,
  IdleLayoutTile,
} from './state.ts';

/** A small seeded generator (mulberry32), so a layout is the same on every device. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rect = (r: IdleLayoutRect): IdleLayoutRect => ({ x: r.x, y: r.y, w: r.w, h: r.h });

/** Kept apart from the layout's own draw, so adding a river moves nothing else (D287). */
const RIVER_SALT = 0x5f3759df;

/** D306, D319: the river (or the stream that becomes it, D287) never sits further than this
 * from the fence, whatever the margin holds. */
export function riverMaxOut(margin: number): number {
  return Math.min(2, margin);
}

/**
 * Water running the length of one side, outside the world: west, east or along the front, never
 * behind. Joined edge to edge, with a bend now and then.
 */
function walkWater(
  rnd: () => number,
  W: number,
  H: number,
  M: number,
  maxOut = M,
  /** D344: a kind picks the side itself; the plan's own worlds leave it to the draw. */
  given?: number,
): IdleLayoutTile[] {
  const water: IdleLayoutTile[] = [];
  // West, east or along the front; never behind, where the marks' rows grow.
  const side = given ?? Math.floor(rnd() * 3);
  // D319: bounded by maxOut, not the full margin, so the run never overhangs a fence corner
  // by more than the river is ever allowed to sit out from the fence along its length.
  const along =
    side === 2 ? { from: -maxOut, to: W + maxOut - 1 } : { from: 0, to: H + maxOut - 1 };
  // How far out from the world's edge: 1 to maxOut.
  let out = 1 + Math.floor(rnd() * maxOut);
  const at = (a: number, o: number): IdleLayoutTile =>
    side === 0 ? { x: -o, y: a } : side === 1 ? { x: W - 1 + o, y: a } : { x: a, y: H - 1 + o };
  for (let a = along.from; a <= along.to; a++) {
    water.push(at(a, out));
    const r = rnd();
    const next = r < 0.2 ? out - 1 : r > 0.8 ? out + 1 : out;
    if (next >= 1 && next <= maxOut && next !== out) {
      // A bend: the turn tile keeps the water joined edge to edge.
      out = next;
      water.push(at(a, out));
    }
  }
  return water;
}

/**
 * The layout with its river (D287): its own, its stream, or one drawn from its seed. Trees on the
 * river's course are left out. Returns the same layout when it already has a river. D348: a
 * theme with no water outside the fence has an empty river.
 */
function withRiver(layout: IdleLayout, content: IdleContent): IdleLayout {
  if (layout.river) return layout;
  if (!hasWater(content)) return { ...layout, river: [] };
  const river =
    layout.stream.length > 0
      ? layout.stream.map((t) => ({ x: t.x, y: t.y }))
      : // D306: close outside the fence, not loose in the margin.
        walkWater(
          seeded((layout.seed ^ RIVER_SALT) >>> 0),
          layout.width,
          layout.height,
          layout.margin,
          riverMaxOut(layout.margin),
        );
  const wet = new Set(river.map((t) => `${t.x},${t.y}`));
  const trees = layout.trees.filter((t) => !wet.has(`${t.x},${t.y}`));
  return { ...layout, trees: trees.length === layout.trees.length ? layout.trees : trees, river };
}

/** D348: whether the theme runs water along a side outside the fence. */
function hasWater(content: IdleContent): boolean {
  return content.map.terrain.water === 'edge';
}

/** D348: how many single things (trees on a world) stand outside the fence: none without a kind. */
function scatterCount(content: IdleContent, drawn: number): number {
  return content.map.terrain.scatter.length > 0 ? drawn : 0;
}

/** Kept apart from the layout's own draw, so placing new land moves nothing else (D301). */
const LAND_SALT = 0x2545f491;

/**
 * D300: an older layout made from a smaller plan gains the plan's new plots. When its river runs
 * along the front, the front margin (water and trees) moves out by the rows the world has grown,
 * so the new row is dry; otherwise trees standing on the new row are cleared.
 */
function grown(layout: IdleLayout, content: IdleContent): IdleLayout {
  // D344: a kind's world was packed from every plot the content had when it was made; it is
  // never stretched to the plan's size, so a producer added since has no plot on it yet.
  if (layout.kind) return layout;
  const m = content.map;
  const missing = Object.keys(m.plots).filter((id) => !layout.plots[id]);
  const delta = Math.max(0, m.height - layout.height);
  if (missing.length === 0 && delta === 0) return layout;
  const { width: W, height: H } = layout;
  const front = (t: IdleLayoutTile) => t.y >= H && t.x >= 0 && t.x < W;
  const river = layout.river ?? layout.stream;
  const moveOut = delta > 0 && river.some(front);
  const shift = (t: IdleLayoutTile) => (t.y >= H ? { x: t.x, y: t.y + delta } : t);
  const plots = { ...layout.plots };
  for (const id of missing) {
    const r = m.plots[id]!;
    plots[id] = layout.mirrored ? { ...r, x: W - r.x - r.w } : rect(r);
  }
  const inBand = (t: IdleLayoutTile) => t.y >= H && t.y < H + delta && t.x >= 0 && t.x < W;
  return {
    ...layout,
    height: H + delta,
    plots,
    trees: moveOut ? layout.trees.map(shift) : layout.trees.filter((t) => !inBand(t)),
    stream: moveOut ? layout.stream.map(shift) : layout.stream,
    ...(layout.river ? { river: moveOut ? layout.river.map(shift) : layout.river } : {}),
  };
}

/** The side the river runs along: west (0), east (1) or the front (2). */
export function riverSide(layout: IdleLayout): number {
  const river = layout.river ?? layout.stream;
  if (river.some((t) => t.y >= layout.height && t.x >= 0 && t.x < layout.width)) return 2;
  return river.some((t) => t.x < 0) ? 0 : 1;
}

/**
 * How far out the river's farthest bend on `side` runs, so land set beyond it never crosses
 * the water. 0 when the river is not on that side.
 */
export function riverReach(layout: IdleLayout, side: number): number {
  const river = layout.river ?? layout.stream;
  const { width: W, height: H } = layout;
  let out = 0;
  for (const t of river) {
    const o =
      side === 0 && t.x < 0
        ? -t.x
        : side === 1 && t.x >= W
          ? t.x - W + 1
          : side === 2 && t.y >= H
            ? t.y - H + 1
            : 0;
    if (o > out) out = o;
  }
  return out;
}

/**
 * D301, D306: each piece of land the content has, set on the river's own side, just past its
 * farthest bend, so the water runs between the fence and the meadow rather than crossing empty
 * ground. A side with no river (there is only ever one) takes the seed's own pick instead. A
 * land already placed stays where it is.
 */
function withLands(layout: IdleLayout, content: IdleContent): IdleLayout {
  const todo = content.map.lands.filter((l) => !layout.lands?.[l.id]);
  if (todo.length === 0) return layout;
  const { width: W, height: H } = layout;
  const lands: Record<string, IdleLayoutLand> = { ...layout.lands };
  let trees = layout.trees;
  const wet = riverSide(layout);
  const sides = [wet, ...[0, 1, 2].filter((x) => x !== wet)];
  content.map.lands.forEach((l, i) => {
    if (lands[l.id]) return;
    const rnd = seeded((layout.seed ^ LAND_SALT ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0);
    // The first land takes the river's own side, by the water; later lands take another.
    const side = sides[i % sides.length]!;
    const reach = riverReach(layout, side);
    const along = (span: number) => Math.floor(rnd() * Math.max(1, span + 1));
    // Touching the farthest riverbank, or one off the world's edge when there is no river here.
    const out = reach + 1;
    const at: IdleLayoutRect =
      side === 2
        ? { x: along(W - l.w), y: H + out - 1, w: l.w, h: l.h }
        : { x: side === 0 ? -out - l.w + 1 : W + out - 1, y: along(H - l.h), w: l.w, h: l.h };
    const plots: Record<string, IdleLayoutRect> = {};
    for (const [id, r] of Object.entries(l.plots))
      plots[id] = { x: at.x + r.x, y: at.y + r.y, w: r.w, h: r.h };
    lands[l.id] = { ...at, plots };
    trees = trees.filter(
      (t) => t.x < at.x || t.x >= at.x + at.w || t.y < at.y || t.y >= at.y + at.h,
    );
  });
  return { ...layout, lands, trees };
}

/** The layout fitted to the content: the river, any new plots, and the lands (D287, D300, D301). */
function fitted(layout: IdleLayout, content: IdleContent): IdleLayout {
  return withLands(grown(withRiver(layout, content), content), content);
}

/** The default plan with no river yet. */
function plainLayout(content: IdleContent): IdleLayout {
  const m = content.map;
  const plots: Record<string, IdleLayoutRect> = {};
  for (const [id, r] of Object.entries(m.plots)) plots[id] = rect(r);
  return withFillers({
    seed: 0,
    width: m.width,
    height: m.height,
    margin: m.margin,
    mirrored: false,
    yard: rect(m.yard),
    plots,
    marks: { x: m.marks.x, y: m.marks.y, dx: 2, dy: -2, perRow: m.marks.perRow },
    trees: [],
    stream: [],
  });
}

/** D440: the layout with its lone tiles listed, each to take a filler; none listed when none. */
function withFillers(l: IdleLayout): IdleLayout {
  const { fillers: _, ...rest } = l;
  const lone = idleLoneTiles(rest);
  return lone.length > 0 ? { ...rest, fillers: lone } : rest;
}

/**
 * The default plan, unflipped, with no trees and no stream: the map every world had before D267,
 * with the river D287 gives it.
 */
export function idleDefaultLayout(content: IdleContent): IdleLayout {
  return fitted(plainLayout(content), content);
}

/**
 * The layout a seed gives. Deterministic: the same content and seed give the same layout.
 * D344: the seed picks a kind of world and lays its own outline and fields; content with no
 * kinds keeps the plan's layout (`idleClassicLayoutFor`).
 */
export function idleLayoutFor(content: IdleContent, seed: number): IdleLayout {
  if (content.map.kinds.length === 0) return idleClassicLayoutFor(content, seed);
  for (let draw = 0; draw < KIND_DRAWS; draw++) {
    const l = kindLayout(content, seed >>> 0, draw);
    if (l) return l;
  }
  return idleClassicLayoutFor(content, seed);
}

/**
 * The layout a seed gave before D344: the plan, flipped or not, with trees and a stream round
 * it. Worlds made then keep the layout stored from it.
 */
export function idleClassicLayoutFor(content: IdleContent, seed: number): IdleLayout {
  const rnd = seeded(seed);
  const base = plainLayout(content);
  const { width: W, height: H, margin: M } = base;
  const mirrored = rnd() < 0.5;
  const flip = (r: IdleLayoutRect): IdleLayoutRect => (mirrored ? { ...r, x: W - r.x - r.w } : r);
  const plots: Record<string, IdleLayoutRect> = {};
  for (const [id, r] of Object.entries(base.plots)) plots[id] = flip(r);
  const marks = mirrored ? { ...base.marks, x: W - base.marks.x - 2, dx: -2 } : base.marks;

  // D306, D319: drawn close to the fence, since the stream becomes the river where there is one.
  const stream: IdleLayoutTile[] =
    rnd() < content.map.streamChance && hasWater(content)
      ? walkWater(rnd, W, H, M, riverMaxOut(M))
      : [];

  const water = new Set(stream.map((t) => `${t.x},${t.y}`));
  const spots: IdleLayoutTile[] = [];
  for (let y = 0; y < H + M; y++)
    for (let x = -M; x < W + M; x++) {
      const inside = x >= 0 && x < W && y < H;
      if (!inside && !water.has(`${x},${y}`)) spots.push({ x, y });
    }
  const t = content.map.trees;
  const count = scatterCount(
    content,
    Math.min(spots.length, t.min + Math.floor(rnd() * (t.max - t.min + 1))),
  );
  // A partial shuffle: the first `count` spots, drawn without repeats.
  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(rnd() * (spots.length - i));
    [spots[i], spots[j]] = [spots[j]!, spots[i]!];
  }
  const trees = spots.slice(0, count).sort((a, b) => a.y - b.y || a.x - b.x);

  return fitted(
    withFillers({
      ...base,
      seed: seed >>> 0,
      mirrored,
      yard: flip(base.yard),
      plots,
      marks,
      trees,
      stream,
    }),
    content,
  );
}

/**
 * The world's layout: its own, or the default for a world made before D267. Always with a river
 * (D287): a layout stored before it gains one here, the same every time.
 */
export function idleLayout(state: IdleWorldState, content: IdleContent): IdleLayout {
  return state.layout
    ? fitted(idleRelaid(state.layout, content), content)
    : idleDefaultLayout(content);
}

/**
 * D423: a kind's layout stored before its yard grew, laid again from its own seed. D440: a plan
 * layout whose yard is not the plan's, laid again from the plan (seed 0 to the default layout).
 * Any other layout as it is. The same every time, so it may run on every read.
 */
export function idleRelaid(layout: IdleLayout, content: IdleContent): IdleLayout {
  if (!layout.kind) {
    const y = content.map.yard;
    if (layout.yard.w === y.w && layout.yard.h === y.h) return layout;
    return layout.seed === 0
      ? idleDefaultLayout(content)
      : idleClassicLayoutFor(content, layout.seed);
  }
  if (layout.yard.w === YARD_W && layout.yard.h === YARD_H) return layout;
  return idleLayoutFor(content, layout.seed);
}

/** Where the `i`th festival's mark stands: two tiles square (D245, D259). */
export function idleMarkTile(layout: IdleLayout, i: number): IdleLayoutRect {
  const m = layout.marks;
  const row = Math.floor(i / m.perRow);
  return { x: m.x + (i % m.perRow) * m.dx, y: m.y + row * m.dy, w: 2, h: 2 };
}

/* ---------- D344: kinds of world ---------- */

/** The kind's own draw from the seed, apart from its fields', so a redraw keeps the kind. */
const KIND_SALT = 0x3c6ef372;
/** Each redraw of a kind's fields is its own draw from the seed. */
const DRAW_SALT = 0x9e3779b9;
/** Draws tried before a seed falls back to the plan's own layout. */
const KIND_DRAWS = 24;
/** The yard's width and depth on a kind's world (D423: room for the heart). */
const YARD_W = 6;
const YARD_H = 6;
/** The depth of the back strip the first marks stand on; fields start below it. */
const BACK = 3;
/** A field on a kind's world is never narrower than this, nor wider. */
const FIELD_MIN = 2;
const FIELD_MAX = 8;
/** D440: the lone tiles' own draw from the seed, so leaving one moves nothing else. */
const FILLER_SALT = 0x6a09e667;

type WorldKind = IdleContent['map']['kinds'][number];

const key = (t: IdleLayoutTile) => `${t.x},${t.y}`;

function tilesOf(r: IdleLayoutRect): IdleLayoutTile[] {
  const out: IdleLayoutTile[] = [];
  for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) out.push({ x, y });
  return out;
}

/** An index into `weights`, drawn by weight. */
function pick(rnd: () => number, weights: readonly number[]): number {
  const total = weights.reduce((a, w) => a + w, 0);
  let r = rnd() * total;
  let last = 0;
  for (let i = 0; i < weights.length; i++) {
    if (weights[i]! <= 0) continue;
    last = i;
    r -= weights[i]!;
    if (r < 0) return i;
  }
  return last;
}

/** D344: the kind of world a seed picks, by weight; none when the content has no kinds. */
export function idleKindFor(content: IdleContent, seed: number): WorldKind | undefined {
  const kinds = content.map.kinds;
  if (kinds.length === 0) return undefined;
  return kinds[
    pick(
      seeded((seed ^ KIND_SALT) >>> 0),
      kinds.map((k) => k.weight),
    )
  ];
}

/**
 * D344: whether a tile is on the world: inside its width and depth and not cut from its outline.
 * A world with no cuts is the whole rectangle.
 */
export function idleOnWorld(layout: IdleLayout): (x: number, y: number) => boolean {
  const cut = new Set((layout.cuts ?? []).flatMap(tilesOf).map(key));
  return (x, y) =>
    x >= 0 && x < layout.width && y >= 0 && y < layout.height && !cut.has(`${x},${y}`);
}

/**
 * One draw of a kind's world (D344), or null when it fails a check. Laid out with the yard to
 * the west and flipped after when the draw says so, as the plan's worlds are. The fields go in
 * bands from the back, in the order the producers open, so the world grows outward as before
 * (D259); a band may be set in from a side the river does not run along, and the last band's
 * spare ground is left outside the fence. D423: bands beside the yard start at its east edge, are
 * never set in on its side, and end level with its front.
 */
function kindLayout(content: IdleContent, seed: number, draw: number): IdleLayout | null {
  const kind = idleKindFor(content, seed)!;
  const m = content.map;
  const rnd = seeded((seed ^ Math.imul(draw + 1, DRAW_SALT)) >>> 0);
  const M = m.margin;
  const W = kind.width[0] + Math.floor(rnd() * (kind.width[1] - kind.width[0] + 1));
  const mirrored = rnd() < 0.5;
  const side = pick(rnd, kind.riverSides);
  // The river's side before the flip: west and east swap.
  const fs = side < 2 && mirrored ? 1 - side : side;

  interface Item {
    id?: string;
    area: number;
    w?: number;
  }
  const items: Item[] = content.producers
    .filter((p) => m.plots[p.id])
    .map((p) => {
      const r = m.plots[p.id]!;
      const jitter = 1 + kind.jitter * (2 * rnd() - 1);
      return { id: p.id, area: Math.max(6, Math.round(r.w * r.h * kind.fieldScale * jitter)) };
    });
  if (rnd() < kind.pond)
    items.splice(2 + Math.floor(rnd() * (items.length - 2)), 0, {
      area: 0,
      w: 2 + Math.floor(rnd() * 2),
    });

  const plots: Record<string, IdleLayoutRect> = {};
  let pond: IdleLayoutRect | undefined;
  const cuts: IdleLayoutRect[] = [];
  // Fields fill from the river's side, so that side's fence stays straight (D306, D319).
  const fromEast = fs === 1;
  let y = BACK;
  let i = 0;
  while (i < items.length) {
    const drawn = kind.bands[Math.floor(rnd() * kind.bands.length)]!;
    const inset = rnd() < kind.notch ? 1 + Math.floor(rnd() * 2) : 0;
    const eastIn = fs === 0 || (fs === 2 && rnd() < 0.5);
    // D423: beside the yard, the band runs from its east edge to the fence and ends at its front.
    const byYard = y < YARD_H;
    const h = byYard ? Math.min(drawn, YARD_H - y) : drawn;
    const base = byYard ? YARD_W : 0;
    const x0 = eastIn || byYard ? base : inset;
    const x1 = eastIn ? W - inset : W;
    let rem = x1 - x0;
    const band: IdleLayoutRect[] = [];
    while (rem > 0 && i < items.length) {
      const it = items[i]!;
      let w = it.w ?? Math.min(FIELD_MAX, Math.max(FIELD_MIN, Math.round(it.area / h)));
      if (w > rem) {
        if (band.length > 0 && rem < Math.max(FIELD_MIN, Math.ceil(w * 0.6))) break;
        w = rem;
      }
      if (rem - w > 0 && rem - w < FIELD_MIN) w = rem;
      const r = { x: fromEast ? x0 + rem - w : x1 - rem, y, w, h };
      rem -= w;
      band.push(r);
      if (it.id) plots[it.id] = r;
      else pond = r;
      i++;
    }
    // A band that ran short shares its spare ground among its fields; the last band's is left
    // outside the fence, unless the river runs along the front, where the fence stays straight.
    if (rem > 0 && (i < items.length || fs === 2)) {
      const n = band.length;
      band.forEach((r, j) => (r.w += Math.floor(rem / n) + (j < rem % n ? 1 : 0)));
      let x = fromEast ? x1 : x0;
      for (const r of band) {
        if (fromEast) x -= r.w;
        r.x = x;
        if (!fromEast) x += r.w;
      }
    }
    const lo = Math.min(...band.map((r) => r.x));
    const hi = Math.max(...band.map((r) => r.x + r.w));
    if (lo > base) cuts.push({ x: base, y, w: lo - base, h });
    if (hi < W) cuts.push({ x: hi, y, w: W - hi, h });
    y += h;
  }
  const H = y;

  const cut = new Set(cuts.flatMap(tilesOf).map(key));
  const onWorld = (x: number, y: number) =>
    x >= 0 && x < W && y >= 0 && y < H && !cut.has(`${x},${y}`);
  carveLone(content, seed, draw, Object.values(plots), (rects) => {
    const marks = Array.from({ length: Math.floor((W - YARD_W) / 2) }, (_, i) => ({
      x: YARD_W + 2 * i,
      y: 0,
      w: 2,
      h: 2,
    }));
    const all = [{ x: 0, y: 0, w: YARD_W, h: YARD_H }, ...rects, ...(pond ? [pond] : []), ...marks];
    return loneTiles(all, onWorld, W, H);
  });
  const river = hasWater(content) ? walkWater(rnd, W, H, M, riverMaxOut(M), fs) : [];
  const wet = new Set(river.map(key));

  // Trees outside the fence, likelier on the wooded side: the side away from the river.
  const woodsEast = fs === 0 ? true : fs === 1 ? false : rnd() < 0.5;
  const spots: IdleLayoutTile[] = [];
  const weights: number[] = [];
  for (let ty = 0; ty < H + M; ty++)
    for (let tx = -M; tx < W + M; tx++) {
      if (onWorld(tx, ty) || wet.has(`${tx},${ty}`)) continue;
      spots.push({ x: tx, y: ty });
      weights.push(woodsEast === tx >= W / 2 ? kind.woods : 1);
    }
  const count = scatterCount(
    content,
    Math.min(
      spots.length,
      kind.trees.min + Math.floor(rnd() * (kind.trees.max - kind.trees.min + 1)),
    ),
  );
  const trees: IdleLayoutTile[] = [];
  for (let n = 0; n < count; n++) {
    const j = pick(rnd, weights);
    trees.push(spots[j]!);
    weights[j] = 0;
  }

  const fx = (r: IdleLayoutRect): IdleLayoutRect => (mirrored ? { ...r, x: W - r.x - r.w } : r);
  const ft = (t: IdleLayoutTile): IdleLayoutTile => (mirrored ? { x: W - 1 - t.x, y: t.y } : t);
  const flipped: Record<string, IdleLayoutRect> = {};
  for (const [id, r] of Object.entries(plots)) flipped[id] = fx(r);
  const layout: IdleLayout = {
    seed,
    width: W,
    height: H,
    margin: M,
    mirrored,
    yard: fx({ x: 0, y: 0, w: YARD_W, h: YARD_H }),
    plots: flipped,
    marks: {
      x: mirrored ? W - YARD_W - 2 : YARD_W,
      y: 0,
      dx: mirrored ? -2 : 2,
      dy: -2,
      perRow: Math.floor((W - YARD_W) / 2),
    },
    trees: trees.map(ft).sort((a, b) => a.y - b.y || a.x - b.x),
    stream: [],
    river: river.map(ft),
    kind: kind.id,
    cuts: cuts.map(fx),
    ...(pond ? { pond: fx(pond) } : {}),
  };
  const done = fitted(withFillers(layout), content);
  if (!idleKindChecks(done, content)) return null;
  const traits = traitsOf(done, content);
  // Every world of a kind shows at least one trait of its own beside its kind (D344).
  return traits.length > 0 ? { ...done, traits } : null;
}

/**
 * D440: tiles on the world that none of `rects` covers and whose four neighbours are each covered
 * or off the world, as "x,y" keys in reading order.
 */
function loneTiles(
  rects: readonly IdleLayoutRect[],
  onWorld: (x: number, y: number) => boolean,
  W: number,
  H: number,
): string[] {
  const taken = new Set(rects.flatMap(tilesOf).map(key));
  const open = (x: number, y: number) => onWorld(x, y) && !taken.has(`${x},${y}`);
  const out: string[] = [];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++)
      if (open(x, y) && !open(x - 1, y) && !open(x + 1, y) && !open(x, y - 1) && !open(x, y + 1))
        out.push(`${x},${y}`);
  return out;
}

/** D440: a layout's lone tiles: on the world, clear of the yard, plots, pond and first marks. */
export function idleLoneTiles(l: IdleLayout): string[] {
  const marks: IdleLayoutRect[] = [];
  for (let i = 0; i < l.marks.perRow; i++)
    marks.push({ x: l.marks.x + i * l.marks.dx, y: l.marks.y, w: 2, h: 2 });
  const rects = [l.yard, ...Object.values(l.plots), ...(l.pond ? [l.pond] : []), ...marks];
  return loneTiles(rects, idleOnWorld(l), l.width, l.height);
}

/**
 * D440: by chance from the seed (`content.map.fillers`), leave one lone tile between two fields,
 * and maybe a second. Two fields one above the other that share an end, one a tile wider than the
 * other and at least 3 deep, can leave one: the wider gives up its row along the line, the
 * narrower takes all of it but the tile at the far end. Each field leaves at most one, and a
 * tile is kept only if it is truly lone (`lone` lists the lone tiles of the rects given).
 */
function carveLone(
  content: IdleContent,
  seed: number,
  draw: number,
  rects: IdleLayoutRect[],
  lone: (rects: readonly IdleLayoutRect[]) => string[],
): void {
  const f = content.map.fillers;
  const rnd = seeded((seed ^ FILLER_SALT ^ Math.imul(draw + 1, DRAW_SALT)) >>> 0);
  if (!(rnd() < f.chance)) return;
  const want = rnd() < f.second ? 2 : 1;
  const pairs: [IdleLayoutRect, IdleLayoutRect][] = [];
  for (const a of rects)
    for (const b of rects) {
      if (a.y + a.h !== b.y || Math.abs(a.w - b.w) !== 1) continue;
      if (a.x !== b.x && a.x + a.w !== b.x + b.w) continue;
      if ((a.w > b.w ? a : b).h >= 3) pairs.push([a, b]);
    }
  const used = new Set<IdleLayoutRect>();
  let made = 0;
  while (made < want && pairs.length > 0) {
    const [a, b] = pairs.splice(Math.floor(rnd() * pairs.length), 1)[0]!;
    if (used.has(a) || used.has(b)) continue;
    const before = { a: { ...a }, b: { ...b } };
    const was = lone(rects);
    const wide = a.w > b.w ? a : b;
    const tx = a.x === b.x ? wide.x + wide.w - 1 : wide.x;
    const tile = `${tx},${b.y - (a.w > b.w ? 1 : 0)}`;
    if (a.w > b.w) {
      a.h -= 1;
      b.y -= 1;
      b.h += 1;
    } else {
      a.h += 1;
      b.y += 1;
      b.h -= 1;
    }
    const now = lone(rects);
    if (now.length === was.length + 1 && now.includes(tile) && was.every((k) => now.includes(k))) {
      used.add(a).add(b);
      made++;
    } else {
      Object.assign(a, before.a);
      Object.assign(b, before.b);
    }
  }
}

/**
 * D344's checks on a kind's world: every producer has a plot of at least two tiles each way,
 * and the yard, the plots, the pond and the first row of marks are on the world and never share
 * a tile; the river runs joined, off the world and within two tiles of its fence along its whole
 * length (D319); every piece of land beside the world is off it and dry (D301); trees stand on
 * none of these.
 */
export function idleKindChecks(l: IdleLayout, content: IdleContent): boolean {
  const onWorld = idleOnWorld(l);
  const taken = new Set<string>();
  const firstMarks: IdleLayoutRect[] = [];
  for (let i = 0; i < l.marks.perRow; i++)
    firstMarks.push({ x: l.marks.x + i * l.marks.dx, y: l.marks.y, w: 2, h: 2 });
  for (const id of Object.keys(content.map.plots)) {
    const r = l.plots[id];
    if (!r || r.w < 2 || r.h < 2) return false;
  }
  for (const r of [l.yard, ...Object.values(l.plots), ...(l.pond ? [l.pond] : []), ...firstMarks])
    for (const t of tilesOf(r)) {
      const k = key(t);
      if (!onWorld(t.x, t.y) || taken.has(k)) return false;
      taken.add(k);
    }
  const river = l.river ?? [];
  if (river.length === 0 && hasWater(content)) return false;
  const wet = new Set(river.map(key));
  for (let i = 0; i < river.length; i++) {
    const t = river[i]!;
    if (onWorld(t.x, t.y)) return false;
    if (i > 0 && Math.abs(t.x - river[i - 1]!.x) + Math.abs(t.y - river[i - 1]!.y) !== 1)
      return false;
    let near = false;
    for (let dy = -2; dy <= 2 && !near; dy++)
      for (let dx = -2; dx <= 2 && !near; dx++) near = onWorld(t.x + dx, t.y + dy);
    if (!near) return false;
  }
  for (const land of Object.values(l.lands ?? {}))
    for (const t of tilesOf(land)) {
      const k = key(t);
      if (onWorld(t.x, t.y) || wet.has(k) || taken.has(k)) return false;
      taken.add(k);
    }
  return l.trees.every((t) => !onWorld(t.x, t.y) && !wet.has(key(t)) && !taken.has(key(t)));
}

/** The bends in the river: steps out from or back towards the fence. */
function bendsOf(l: IdleLayout): number {
  const river = l.river ?? l.stream;
  const front = riverSide(l) === 2;
  let n = 0;
  for (let i = 1; i < river.length; i++)
    if (front ? river[i]!.y !== river[i - 1]!.y : river[i]!.x !== river[i - 1]!.x) n++;
  return n;
}

/** D344: the traits that hold for a kind's world, in the content's order, as many as it shows. */
function traitsOf(l: IdleLayout, content: IdleContent): string[] {
  const plots = Object.values(l.plots);
  const areas = plots.map((r) => r.w * r.h);
  const cutTiles = (l.cuts ?? []).reduce((a, r) => a + r.w * r.h, 0);
  const holds = (t: IdleContent['map']['traits'][number]): boolean => {
    switch (t.rule) {
      case 'treesAtLeast':
        return l.trees.length >= t.at;
      case 'treesAtMost':
        return l.trees.length <= t.at;
      case 'biggestField':
        return Math.max(...areas) >= t.at;
      case 'smallestField':
        return Math.min(...areas) <= t.at;
      case 'pond':
        return l.pond !== undefined;
      case 'riverBends':
        return bendsOf(l) >= t.at;
      case 'cutTiles':
        return cutTiles >= t.at;
      case 'longField':
        return plots.some((r) => Math.max(r.w, r.h) >= t.at * Math.min(r.w, r.h));
    }
  };
  return content.map.traits
    .filter(holds)
    .slice(0, content.map.maxTraits - 1)
    .map((t) => t.id);
}

/**
 * D344: the world's kind and traits as stored, by id, its kind first. Empty for a world made
 * before kinds, which has neither.
 */
export function idleLandTraitIds(layout: IdleLayout | undefined): string[] {
  return layout?.kind ? [layout.kind, ...(layout.traits ?? [])] : [];
}

/** D344: the names of kind and trait ids, as the player reads them. Unknown ids are left out. */
export function idleLandTraitNames(content: IdleContent, ids: readonly string[]): string[] {
  const names = new Map<string, string>();
  for (const k of content.map.kinds) names.set(k.id, k.name);
  for (const t of content.map.traits) names.set(t.id, t.name);
  return ids.flatMap((id) => (names.has(id) ? [names.get(id)!] : []));
}
